import { ColumnsType } from "antd/es/table";
import { TelegramBotConfig } from "@lib/types/TelegramChatBot";
import { MessageFormatElement } from "react-intl";
import { ColumnConfig, generateColumns } from "@lib/helpers/TableHelper";
import TableIdButton from "@lib/components/MiniComponents/Buttons/TableIdButton";
import { shortenString } from "@lib/helpers/StringHelper";
import { Switch, Tag } from "antd";
import { Link, NavigateFunction } from "react-router-dom";
import { UseMutationProps } from "@lib/types/Api";
import dayjs from "dayjs";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { FaTelegram } from "react-icons/fa";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { AppConst, ROUTES } from "@lib/constants";
import { getRoute } from "@lib/helpers/RouteHelper";

export type ITelegramBotConfigColumnProps = {
  handleSelect: (config: TelegramBotConfig) => void;
  handleEditTelegramBotConfig: (
    values: TelegramBotConfig,
    enabled?: boolean,
  ) => void;
  deleteQuery: UseMutationProps<any>;
  messages: Record<string, string> | Record<string, MessageFormatElement[]>;
  navigate: NavigateFunction;
};

const getColumns = <T extends TelegramBotConfig>(
  props: ITelegramBotConfigColumnProps,
): ColumnsType<T> => {
  const { messages, handleSelect, handleEditTelegramBotConfig, deleteQuery } =
    props;

  const columnsConfig: ColumnConfig<T>[] = [
    {
      title: "ID",
      dataIndex: "id",
      width: "160px",
      render: (id) => (
        <TableIdButton text={`${shortenString(id)}`} textToBeCopied={id} />
      ),
    },
    {
      title: "common.name",
      width: "200px",
      dataIndex: "name",
      filterType: "search",
      render: (_, value) => {
        if (value?.botUrl) {
          return (
            <Link to={value?.botUrl} target="_blank" data-testid="name">
              {value?.name || "N/A"}
            </Link>
          );
        }
        return <span data-testid="name">{value?.name || "N/A"}</span>;
      },
    },
    {
      title: "common.token",
      dataIndex: "token",
      width: "200px",
      filterType: "search",
      render: (value: string) => (
        <TableIdButton
          text={`${shortenString(value)}`}
          textToBeCopied={value}
        />
      ),
    },
    {
      title: "common.appUrl",
      width: "200px",
      dataIndex: "appUrl",
      filterType: "search",
      render: (_, record: TelegramBotConfig) => {
        const path = `/wallet?${AppConst.TELEGRAM_BOT_QUERY_ID}=${record.id}`;
        let url = record.appUrl;
        try {
          url = `${new URL(record.appUrl)?.origin}${path}`;
        } catch (err) {
          url = `${url.replace(/\/$/, "")}${path}`;
        }
        return (
          <TableIdButton
            text={`${shortenString(url, 20, true)}`}
            textToBeCopied={url}
          />
        );
      },
    },
    {
      title: "common.status",
      dataIndex: "enabled",
      width: "100px",
      render: (_, record) => (
        <Switch
          size="default"
          checkedChildren={messages["common.enabled"] as string}
          unCheckedChildren={messages["common.disabled"] as string}
          checked={record.enabled}
          onChange={(checked: boolean) => {
            // console.log("🚀 ~ record:", record);
            handleEditTelegramBotConfig(record, checked);
          }}
        />
      ),
      filterType: "dropdown",
      filters: [
        { text: messages["common.enabled"] as string, value: true },
        { text: messages["common.disabled"] as string, value: false },
      ],
    },
    {
      title: "common.paymentProviders",
      dataIndex: "paymentProviders",
      width: "200px",
      render: (_, record) => {
        const tagValues = record?.paymentProviders?.map(
          (payment) => payment.name,
        );
        if (tagValues && tagValues?.length > 0) {
          return <Tag>{tagValues.join(", ")}</Tag>;
        }
        return <Tag color="red">{messages["common.noPayment"] as string}</Tag>;
      },
    },
    {
      title: "common.createdAt",
      dataIndex: "createdAt",
      width: "180px",
      render: (d) => dayjs(d).format("DD/MM/YYYY"),
    },
    {
      title: "common.updatedAt",
      dataIndex: "updatedAt",
      width: "180px",
      render: (d) => dayjs(d).format("DD/MM/YYYY"),
    },
    {
      title: "common.actions",
      dataIndex: "actions",
      className: "order-table-action",
      fixed: "right",
      width: "170px",
      render: (_, record) => (
        <StyledFlex className="actions">
          <ActionButton
            type="link"
            icon={<FaTelegram size={16} />}
            color="orange"
            href={record.botUrl}
            targetBlank
          />

          <ActionButton
            type="detail"
            noLabel
            href={getRoute(
              ROUTES.ADMIN.TELEGRAM_BOT.TELEGRAM_BOT_CONFIG.LIST_TAB,
              {
                configId: record.id,
              },
            )}
          />

          <ActionButton
            type="edit"
            noLabel
            onClick={() => handleSelect(record)}
          />

          <ActionButton
            type="delete"
            noLabel
            itemName={record.name}
            onClick={() => {
              deleteQuery.mutate(record?.id);
            }}
          />
        </StyledFlex>
      ),
    },
  ];

  return generateColumns(props, columnsConfig);
};

export default getColumns;
